import logger from '../utils/logger';
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin, ArrowRight, Heart, Users, Target, ShieldCheck, Clock, TrendingUp, ImageIcon } from 'lucide-react';
import axios from 'axios';
import GlobalCandleWall from '../components/GlobalCandleWall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const COUNTRY_FLAGS = {
  'Haiti': '\u{1F1ED}\u{1F1F9}',
  'Colombia': '\u{1F1E8}\u{1F1F4}',
  'El Salvador': '\u{1F1F8}\u{1F1FB}',
  'Ecuador': '\u{1F1EA}\u{1F1E8}',
  'Jamaica': '\u{1F1EF}\u{1F1F2}',
  'Dominican Republic': '\u{1F1E9}\u{1F1F4}',
};

const International = () => {
  const [programs, setPrograms] = useState([]);
  const [globalImpact, setGlobalImpact] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [programsRes, impactRes, noticeRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/country-programs`),
        axios.get(`${BACKEND_URL}/api/public/global-impact`),
        axios.get(`${BACKEND_URL}/api/public/site-content/global_program_notice`).catch(() => ({ data: { data: {} } })),
      ]);
      setPrograms(programsRes.data);
      setGlobalImpact(impactRes.data);
      setNotice(noticeRes.data?.data || null);
    } catch (error) {
      logger.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const activePrograms = programs.filter(p => p.status === 'active' || p.status === 'pending');
  const comingSoonPrograms = programs.filter(p => p.status === 'coming_soon');

  return (
    <div>
      {/* Header */}
      <section className="bg-[#0A0A0A] py-12" data-testid="international-header">
        <div className="px-6 text-center">
          <p className="text-xs uppercase tracking-[0.2em] text-[#BA1826] font-semibold mb-2">
            Global Programs
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-3">
            Global Family Support Programs
          </h1>
          <p className="text-gray-400 text-sm max-w-md mx-auto">
            Supporting families across multiple countries with consistent, transparent, and dignity-first care.
          </p>
        </div>
      </section>

      {/* Global Impact Dashboard */}
      {globalImpact && (
        <section className="bg-[#0A0A0A] py-8" data-testid="global-impact-dashboard">
          <div className="px-6">
            <div className="flex items-center justify-center gap-2 mb-5">
              <TrendingUp className="w-5 h-5 text-[#BA1826]" />
              <h2 className="font-heading text-lg font-bold text-white">Global Impact Across All Programs</h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-white">{globalImpact.active_countries}</div>
                <span className="text-gray-400 text-xs">Active Countries</span>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-white">{globalImpact.total_families_target}</div>
                <span className="text-gray-400 text-xs">Families Targeted</span>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-white">{globalImpact.total_families_supported}</div>
                <span className="text-gray-400 text-xs">Families Supported</span>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg">
                <div className="text-2xl font-bold text-[#BA1826]">${globalImpact.total_raised.toLocaleString()}</div>
                <span className="text-gray-400 text-xs">Total Raised</span>
              </div>
              <div className="text-center p-3 bg-white/5 rounded-lg col-span-2 md:col-span-1">
                <div className="text-2xl font-bold text-white">${globalImpact.total_monthly_goal.toLocaleString()}</div>
                <span className="text-gray-400 text-xs">Combined Monthly Goal</span>
              </div>
            </div>
            <div className="max-w-xl mx-auto">
              <div className="relative h-4 bg-white/10 rounded-full overflow-hidden">
                <div 
                  className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#BA1826] to-[#E53935] rounded-full transition-all duration-700"
                  style={{ width: `${globalImpact.progress_percentage}%` }}
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-[10px] font-semibold text-white">
                    {globalImpact.progress_percentage.toFixed(0)}% of combined goal
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Bridge Statement */}
      <div className="bg-white py-5 border-b border-[#E5E7EB]">
        <p className="text-sm text-[#4B5563] text-center px-6 max-w-lg mx-auto">
          Our work extends beyond New York City, reaching communities across multiple countries.
        </p>
        {/* Temporary Expansion Notice (admin-editable via CMS: global_program_notice).
            Scheduling: shows only if today >= show_after (if set) AND today < hide_after (if set). */}
        {(() => {
          if (!notice?.enabled || !notice?.message) return null;
          const todayStr = new Date().toISOString().slice(0, 10);
          if (notice.show_after && todayStr < notice.show_after) return null;
          if (notice.hide_after && todayStr >= notice.hide_after) return null;
          return (
            <div className="max-w-2xl mx-auto px-6 mt-4" data-testid="global-program-notice">
              <div className="bg-[#F8F9FA] border-l-2 border-[#BA1826] rounded-sm py-3 px-4">
                {notice.label && (
                  <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-[#BA1826] mb-1 flex items-center gap-1.5">
                    <Globe className="w-3 h-3" />
                    {notice.label}
                  </p>
                )}
                <p className="text-xs text-[#4B5563] leading-relaxed italic">
                  {notice.message}
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Hero */}
      <section className="py-10 bg-white" data-testid="international-hero">
        <div className="px-6 text-center">
          <p className="text-sm uppercase tracking-[0.15em] text-[#BA1826] font-semibold mb-3">
            Choose Where Your Support Goes
          </p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-4">
            Country-Based Giving Programs
          </h2>
          <p className="text-[#4B5563] max-w-xl mx-auto leading-relaxed text-sm">
            Each country program is tracked independently so donors can see exactly how their 
            contribution helps families. Choose a program below to learn more and donate directly 
            to the community you want to support.
          </p>
        </div>
      </section>

      {/* Transparency Banner */}
      <section className="bg-[#0A0A0A] py-5" data-testid="transparency-banner">
        <div className="px-6 flex items-center justify-center gap-3">
          <ShieldCheck className="w-5 h-5 text-[#BA1826]" />
          <p className="text-sm text-white">
            Every country fund is tracked separately — you always know where your support goes.
          </p>
        </div>
      </section>

      {/* Active Programs */}
      {activePrograms.length > 0 && (
        <section className="py-10 bg-[#F8F9FA]" data-testid="active-programs-section">
          <div className="px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-[#22C55E]"></div>
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A]">Active Programs</h3>
            </div>

            <div className="space-y-5">
              {activePrograms.map((program) => {
                const progress = program.monthly_goal > 0 
                  ? Math.min((program.amount_raised / program.monthly_goal) * 100, 100) 
                  : 0;
                const familiesFromDonations = program.cost_per_family > 0 
                  ? Math.floor(program.amount_raised / program.cost_per_family) 
                  : 0;

                return (
                  <div
                    key={program.id}
                    className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    data-testid={`program-card-${program.id}`}
                  >
                    <div className="md:flex">
                      {/* Flag Display */}
                      <div className="md:w-56 h-48 md:h-auto flex-shrink-0 bg-gradient-to-br from-[#F8F9FA] to-[#EEF2FF] border-r border-[#E2E8F0] flex flex-col items-center justify-center p-6" data-testid={`program-image-${program.id}`}>
                        <span className="text-7xl mb-2" role="img" aria-label={`${program.country_name} flag`}>
                          {COUNTRY_FLAGS[program.country_name] || '\u{1F30D}'}
                        </span>
                        <p className="text-xs font-semibold text-[#4B5563] text-center mt-1">{program.country_name}</p>
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-[#BA1826]" />
                              <span className="text-xs uppercase tracking-wider text-[#4B5563] font-medium">
                                {COUNTRY_FLAGS[program.country_name] || ''} {program.country_name} — {program.program_city}
                              </span>
                            </div>
                            <h4 className="font-heading text-lg font-bold text-[#0A0A0A]">
                              {program.program_title}
                            </h4>
                          </div>
                          <span className={`text-xs font-semibold px-2 py-1 rounded flex-shrink-0 ${
                            program.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                          }`}>
                            {program.status === 'pending' ? 'Pending' : 'Active'}
                          </span>
                        </div>

                        <p className="text-sm text-[#4B5563] mb-4 line-clamp-2">
                          {program.hero_copy}
                        </p>

                        {/* Stats Row */}
                        <div className="grid grid-cols-4 gap-3 mb-4">
                          <div>
                            <p className="text-xs text-[#4B5563]">Monthly Goal</p>
                            <p className="text-sm font-bold text-[#0A0A0A]">${program.monthly_goal.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#4B5563]">Raised</p>
                            <p className="text-sm font-bold text-[#BA1826]">${program.amount_raised.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#4B5563]">Families</p>
                            <p className="text-sm font-bold text-[#0A0A0A]">{program.families_target}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#4B5563]">Supported</p>
                            <p className="text-sm font-bold text-[#0A0A0A]">{familiesFromDonations}</p>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mb-4">
                          <div className="relative h-3 bg-[#F1F5F9] rounded-full overflow-hidden">
                            <div 
                              className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#BA1826] to-[#E53935] rounded-full transition-all duration-500"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                          <p className="text-[10px] text-[#4B5563] mt-1">{progress.toFixed(0)}% of monthly goal</p>
                        </div>

                        {/* Impact line + CTA */}
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-[#4B5563] italic">{program.impact_line}</p>
                          {program.donations_enabled === false || program.status === 'pending' ? (
                            <span className="text-xs px-4 py-2 bg-gray-100 text-gray-500 rounded-lg font-semibold"
                              data-testid={`support-${program.id}-disabled`}>
                              {program.status === 'pending' ? 'Coming Soon' : 'Launching Soon'}
                            </span>
                          ) : (
                            <Link
                              to={`/international/${program.id}`}
                              className="btn-primary text-xs px-4 py-2"
                              data-testid={`support-${program.id}-btn`}
                            >
                              {program.primary_cta}
                              <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Coming Soon Programs */}
      {comingSoonPrograms.length > 0 && (
        <section className="py-10 bg-white" data-testid="coming-soon-section">
          <div className="px-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
              <h3 className="font-heading text-xl font-bold text-[#0A0A0A]">Coming Soon</h3>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              {comingSoonPrograms.map((program) => (
                <div
                  key={program.id}
                  className="bg-[#F8F9FA] border border-[#E5E7EB] rounded-lg p-5 relative overflow-hidden"
                  data-testid={`coming-soon-card-${program.id}`}
                >
                  <div className="absolute top-0 right-0 bg-[#F59E0B] text-white text-[10px] font-bold px-3 py-1 rounded-bl-lg">
                    COMING SOON
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 bg-[#FEF3C7] rounded-lg flex items-center justify-center text-xl">
                      {COUNTRY_FLAGS[program.country_name] ? (
                        <span role="img" aria-label={`${program.country_name} flag`}>{COUNTRY_FLAGS[program.country_name]}</span>
                      ) : (
                        <Clock className="w-5 h-5 text-[#F59E0B]" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-heading font-bold text-[#0A0A0A]">{program.country_name}</h4>
                      {program.program_city && (
                        <span className="text-xs text-[#4B5563]">{program.program_city}</span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-[#4B5563]">{program.overview_copy}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Global Candle Wall */}
      <GlobalCandleWall maxDisplay={40} />

      {/* How It Works */}
      <section className="py-10 bg-[#F8F9FA]" data-testid="how-it-works">
        <div className="px-6">
          <div className="text-center mb-8">
            <h3 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">
              How Global Family Support Works
            </h3>
          </div>
          <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <div className="text-center p-5 bg-white rounded-lg border border-[#E5E7EB]">
              <div className="w-10 h-10 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-3">
                <Globe className="w-5 h-5 text-[#BA1826]" />
              </div>
              <h4 className="font-heading font-semibold text-sm mb-1">1. Choose a Country</h4>
              <p className="text-xs text-[#4B5563]">Select the program you want to support from our active countries.</p>
            </div>
            <div className="text-center p-5 bg-white rounded-lg border border-[#E5E7EB]">
              <div className="w-10 h-10 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-3">
                <Heart className="w-5 h-5 text-[#BA1826]" />
              </div>
              <h4 className="font-heading font-semibold text-sm mb-1">2. Make Your Gift</h4>
              <p className="text-xs text-[#4B5563]">Give one-time or sponsor a family monthly. Every dollar is tracked to your chosen program.</p>
            </div>
            <div className="text-center p-5 bg-white rounded-lg border border-[#E5E7EB]">
              <div className="w-10 h-10 bg-[#FEE2E2] rounded-full flex items-center justify-center mx-auto mb-3">
                <Users className="w-5 h-5 text-[#BA1826]" />
              </div>
              <h4 className="font-heading font-semibold text-sm mb-1">3. See the Impact</h4>
              <p className="text-xs text-[#4B5563]">Track progress in real-time. We report on families reached and funds used for each country.</p>
            </div>
          </div>
        </div>
      </section>

      {/* General Fund CTA */}
      <section className="py-10 bg-[#BA1826]" data-testid="general-fund-cta">
        <div className="px-6 text-center">
          <h2 className="font-heading text-2xl font-bold text-white mb-3">
            Prefer to Support the General Fund?
          </h2>
          <p className="text-white/80 max-w-md mx-auto mb-6 text-sm">
            Your general donation goes where it's needed most — supporting both local New York 
            initiatives and international programs across all countries.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link
              to="/donate"
              className="bg-white text-[#BA1826] px-6 py-2 font-semibold rounded hover:bg-gray-100 transition-all text-sm"
              data-testid="general-donate-btn"
            >
              Donate to General Fund
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default International;
